import type { StatusColorTone } from '@/styles/statusColors';
import type { UploadMimeType } from '@/types/upload.types';
import type {
  PcmpAccountKind,
  PcmpChannel,
  PcmpEditorRole,
  PcmpLaneTone,
  PcmpMktApprovalKind,
  PcmpMktRejectionType,
  PcmpMktSchoolRejectReason,
  PcmpPaymentType,
  PcmpTimelineStatus,
} from '../types/pcmpProposal.types';

export const PCMP_LANE_TONE_COLOR = {
  DONE: 'success',
  PROGRESS: 'info',
  HOLD: 'warning',
  REJECTED: 'danger',
  NONE: 'neutral',
} as const satisfies Record<PcmpLaneTone, StatusColorTone>;

export const PCMP_TIMELINE_STATUS_COLOR = {
  COMPLETED: 'success',
  REJECTED: 'danger',
  ONHOLD: 'warning',
  RELEASED: 'info',
  EDITED: 'info',
  PENDING: 'warning',
  WAITING: 'neutral',
  OPTIONAL: 'neutral',
} as const satisfies Record<PcmpTimelineStatus, StatusColorTone>;

export const PCMP_CHANNEL_LABELS = {
  ACCOUNTS: 'Accounts',
  MARKETING: 'Marketing',
} as const satisfies Record<PcmpChannel, string>;

export const PCMP_PAYMENT_TYPE_LABELS = {
  BKASH: 'bKash',
  NAGAD: 'Nagad',
  BANK: 'Bank',
  ROCKET: 'Rocket',
  UPAY: 'Upay',
  OTHERS: 'Other MFS',
  CASH: 'Cash',
  GIFT: 'Gift',
} as const satisfies Record<PcmpPaymentType, string>;

// Mirrors the API's PCMP_ACCOUNT_KIND_BY_PAYMENT_TYPE: CASH and GIFT take no account.
export const PCMP_ACCOUNT_KIND_BY_PAYMENT_TYPE = {
  BKASH: 'MFS',
  NAGAD: 'MFS',
  BANK: 'BANK',
  ROCKET: 'MFS',
  UPAY: 'MFS',
  OTHERS: 'MFS',
  CASH: null,
  GIFT: null,
} as const satisfies Record<PcmpPaymentType, PcmpAccountKind | null>;

export const PCMP_EDITOR_ROLE_LABELS = {
  POST_DISBURSEMENT: 'Admin (after disbursement)',
  ANY_STAGE: 'Any-stage editor',
  MARKETING: 'Marketing approver',
  MIS: 'MIS',
  CORRECTION_REQUEST: 'Proposal Correction',
} as const satisfies Record<PcmpEditorRole, string>;

/** The request category a user granted Proposal Correction works; edits made under it roll back if it is rejected. */
export const PCMP_CORRECTION_CATEGORY_CODE = 'PROPOSAL_CORRECTION';

export const PCMP_CORRECTION_COPY = {
  banner:
    'Edits made under this request are recorded in the history. If the request is rejected, every one of them is rolled back; if it is approved, they stay.',
  approve:
    'Once approved, this request is final and cannot be changed again. Every edit made under it stays.',
  reject:
    'Rejecting this request rolls back every change made to the proposal under it. The edits and the rollback both stay in the Activity History.',
} as const;

// Limits mirror the API's edit DTOs (LB-744/745).
export const PCMP_EDIT_LIMITS = {
  reason: 1000,
  submissionComments: 500,
  teacherName: 200,
  shortText: 100,
  longText: 1000,
  holderName: 200,
  maxQty: 9_999_999,
  maxAmount: 999_999_999_999.99,
} as const;

// Printed report header (LB-748). Placeholder text until the final address
// is confirmed.
export const PCMP_REPORT_COMPANY = {
  name: 'Lecture Publications Ltd.',
  address: 'House- 2A, 1st floor, Rd 95, Dhaka 1212',
} as const;

/** Beside Disbursed once part of the payment came back (API PCMP_PARTIALLY_RETURNED_LABEL). */
export const PCMP_PARTIALLY_RETURNED_LABEL = 'Partially Returned';

/** Approve and reject comments and the explanation share the API's event comment cap. */
export const PCMP_MKT_COMMENT_MAX = 1000;

/** The Marketing payment proof: what the storage policy accepts. */
export const PCMP_MKT_PROOF_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'application/pdf',
] as const satisfies readonly UploadMimeType[];

export const PCMP_MKT_PROOF_ACCEPT = '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf';

export const PCMP_MKT_PROOF_MAX_BYTES = 20 * 1024 * 1024;

export const PCMP_MKT_APPROVE_LABEL = {
  LEVEL: (levelNo) => (levelNo === null ? 'Approve' : `Approve (L${levelNo})`),
  ADMIN_FALLBACK: () => 'Approve as Admin',
  ENDORSE: () => 'Endorse',
} as const satisfies Record<PcmpMktApprovalKind, (levelNo: number | null) => string>;

export const PCMP_MKT_REJECTION_TYPE_COPY = {
  TEACHER: {
    label: 'Teacher Reject',
    hint: "Rejects only this proposal. The school's information stays as it is.",
  },
  SCHOOL_WITH_TEACHER: {
    label: 'School with Teacher Reject',
    hint: 'Rejects the school too. It cannot be proposed again under this project.',
  },
} as const satisfies Record<PcmpMktRejectionType, { label: string; hint: string }>;

export const PCMP_MKT_SCHOOL_REJECT_REASON_LABELS = {
  SCHOOL_NOT_ENLISTED: 'School not enlisted',
  INFORMATION_CORRECTION: 'Information correction',
} as const satisfies Record<PcmpMktSchoolRejectReason, string>;
